import React, {useMemo} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PolygonIcon from '@apps-common/ui-icons/Polygon';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Typography,
} from '@mui/material';
import {deselectFeatureRef} from '@actions/selected-features';

import AreaIndicator from '@app/components/common/AreaIndicator';
import CopyButton from '@app/components/common/CopyButton';
import LoadingMessage from '@app/components/common/LoadingMessage';
import MoreActionsMenu from '@components/Features/FeatureActions/MoreActionsMenu';
import {getCollectionIDFromReference} from '@app/util/granero';
import {trackEvent} from '@app/analytics';
import {useDispatch, useSelector} from 'react-redux';
import {useGetCollection} from '@app/api/collections';
import {useGetFeatureByReference} from '@app/api/features';
import {useTranslation} from 'react-i18next';

const Details = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const selectedFeatureRef = useSelector(state => state.selectedFeatureRef);
  const {data: selectedFeature, isLoading: isFeatureLoading} =
    useGetFeatureByReference(
      {
        reference: selectedFeatureRef,
      },
      {
        enabled: !!selectedFeatureRef,
      }
    );
  const collectionId = useMemo(
    () => getCollectionIDFromReference(selectedFeatureRef),
    [selectedFeatureRef]
  );
  const {data: selectedCollection, isLoading: isCollectionLoading} =
    useGetCollection(
      {
        collectionId: collectionId,
      },
      {
        enabled: !!collectionId,
      }
    );

  const isOwner = selectedCollection?.permissions.is_owner;

  const handleClose = () => {
    dispatch(deselectFeatureRef());
  };

  if (!selectedFeatureRef) {
    return false;
  }

  return (
    <Card sx={{width: 300}}>
      <CardHeader
        title={
          <Typography role="title" display="flex" alignItems="center">
            <InfoOutlinedIcon
              fontSize="small"
              color="action"
              sx={{marginRight: 1}}
            />
            {t('detailsPanel.title')}
          </Typography>
        }
        action={
          <>
            <MoreActionsMenu
              feature={selectedFeature}
              isOwner={isOwner}
              showViewDetails={false}
            />
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => handleClose()}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
        sx={{
          paddingLeft: 1,
          paddingY: 1,
          backgroundColor: 'grey.100',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      />
      <CardContent
        sx={{
          '&.MuiCardContent-root:last-child': {
            paddingBottom: 1,
          },
        }}
      >
        {isFeatureLoading || isCollectionLoading ? (
          <LoadingMessage
            progressSize={36}
            titleTypographyProps={{variant: 'h6'}}
          />
        ) : (
          <>
            <Box display="flex" flexDirection="column">
              <Box display="flex" alignItems="center">
                <PolygonIcon fontSize="small" color="action" sx={{mr: 0.5}} />
                <Typography variant="subtitle1">
                  {selectedFeature?.properties?.title}
                </Typography>
              </Box>
              <Typography color="text.secondary" variant="caption">
                <AreaIndicator
                  squareMeters={selectedFeature.properties['pl:area']}
                />
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color={
                selectedFeature?.properties?.description
                  ? 'text.primary'
                  : 'text.secondary'
              }
            >
              {selectedFeature?.properties?.description || (
                <i>no description</i>
              )}
            </Typography>
            {selectedFeatureRef && (
              <>
                <Typography variant="subtitle1" sx={{mt: 2}}>
                  {t('detailsPanel.featureRef')}
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    color="textSecondary"
                    variant="caption"
                    noWrap
                    sx={{
                      bgcolor: 'grey.100',
                      px: 1,
                      py: 1,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedFeatureRef}
                  </Typography>
                  <CopyButton
                    onCopy={() =>
                      trackEvent('Copied feature reference in details panel', {
                        featureRef: selectedFeatureRef,
                      })
                    }
                    size="small"
                    textToCopy={selectedFeatureRef}
                    title={t('detailsPanel.copy')}
                  />
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Details;
